import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "limit", "value", "strokeWidth", "size", "variant"];
import { createContext, useContext } from 'react';
import styled from 'styled-components';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { Heading } from '../Typography';
import { LayoutHorizontal } from '../Layout/LayoutHorizontal';
import { useAnimatedStroke } from './useAnimatedStroke';
import { useGradientColor } from './useGradientColor';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const METER_RATIO = 0.6;
const PATH = 'M 50,50 m -45,0 a 45,45 0 1 1 90,0';
const MeterContext = /*#__PURE__*/createContext({
  percentage: 0
});
const Meter = styled.svg.withConfig({
  displayName: "ProgressMeter__Meter",
  componentId: "gqd3ei-0"
})(["", ";", ";block-size:var(--block-size);fill:none;inline-size:var(--inline-size);inset:0;position:absolute;stroke-linecap:round;", ";"], ({
  inlineSize
}) => `--inline-size: ${inlineSize}`, ({
  blockSize
}) => `--block-size: ${blockSize}`, ({
  strokeWidth
}) => `stroke-width: ${strokeWidth}`);
const Track = styled.path.withConfig({
  displayName: "ProgressMeter__Track",
  componentId: "gqd3ei-1"
})(["", ";", ";transition:stroke-dashoffset 0.75s ease-in;@media (prefers-reduced-motion:reduce){transition:none;}"], ({
  strokeDashArray
}) => `stroke-dasharray: ${strokeDashArray}`, ({
  strokeDashOffset
}) => `stroke-dashoffset: ${strokeDashOffset}`);
export const ProgressMeter = _ref => {
  let {
      children,
      limit = 1,
      value = 0,
      strokeWidth = 6,
      size = '12rem',
      variant = 'info'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    gradient
  } = useGradientColor(variant);
  const percentageValue = value / limit || 0;
  const percentage = Math.min(percentageValue, 1);
  const {
    ref,
    strokeDashArray,
    strokeDashOffset
  } = useAnimatedStroke(percentage);
  const blockSize = `calc(${size} * ${METER_RATIO})`;
  return /*#__PURE__*/_jsx(MeterContext.Provider, {
    value: {
      percentage
    },
    children: /*#__PURE__*/_jsxs(LayoutHorizontal, Object.assign({}, rest, {
      alignItems: "end",
      blockSize: blockSize,
      inlineSize: size,
      justifyContent: "center",
      position: "relative",
      textAlign: "center",
      children: [/*#__PURE__*/_jsxs(Meter, {
        blockSize: blockSize,
        inlineSize: size,
        strokeWidth: strokeWidth,
        viewBox: `0 0 100 ${100 * METER_RATIO}`,
        children: [/*#__PURE__*/_jsx("defs", {
          children: /*#__PURE__*/_jsxs("linearGradient", {
            gradientUnits: "userSpaceOnUse",
            id: "meter-gradient",
            x1: "0.54907",
            x2: "211.316",
            y1: "139.936",
            y2: "75.0169",
            children: [/*#__PURE__*/_jsx("stop", {
              offset: "0.08",
              stopColor: gradient.light
            }), /*#__PURE__*/_jsx("stop", {
              offset: "0.32",
              stopColor: gradient.medium
            }), /*#__PURE__*/_jsx("stop", {
              offset: "0.6",
              stopColor: gradient.default
            }), /*#__PURE__*/_jsx("stop", {
              offset: "1",
              stopColor: gradient.default
            })]
          })
        }), /*#__PURE__*/_jsx("path", {
          d: PATH,
          fillOpacity: "0",
          ref: ref,
          stroke: "var(--layout-lib-border-color-light)",
          strokeWidth: strokeWidth
        }), value ? /*#__PURE__*/_jsx(Track, {
          d: PATH,
          fillOpacity: "0",
          stroke: "url(#meter-gradient)",
          strokeDashArray: strokeDashArray,
          strokeDashOffset: strokeDashOffset,
          strokeWidth: strokeWidth
        }) : null]
      }), children]
    }))
  });
};
const Percentage = () => {
  const {
    percentage
  } = useContext(MeterContext);
  const percentageValue = percentage * 100;
  return /*#__PURE__*/_jsx(Heading, {
    as: "div",
    fontSize: "lg",
    marginBlockEnd: "space-sm",
    children: /*#__PURE__*/_jsx(FormattedPercentage, {
      precision: 1,
      value: percentageValue
    })
  });
};
ProgressMeter.Percentage = Percentage;